/**
 * TitleScreen — atmospheric title screen for The Cats of Chronos.
 *
 * Features:
 * - Game title in elegant gothic styling
 * - Slowly rotating brass gears in the background
 * - Floating bioluminescent particles
 * - Deep jewel-tone gradient background
 * - "Press Any Key to Start" prompt with pulsing animation
 * - Transitions to Level Select on any input
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { FreeCamera } from "@babylonjs/core/Cameras/freeCamera";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { GlowLayer } from "@babylonjs/core/Layers/glowLayer";
import { AdvancedDynamicTexture } from "@babylonjs/gui/2D/advancedDynamicTexture";
import { TextBlock } from "@babylonjs/gui/2D/controls/textBlock";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel";
import { Control } from "@babylonjs/gui/2D/controls/control";
import { Rectangle } from "@babylonjs/gui/2D/controls/rectangle";
import { COLORS } from "../config/GameConfig";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { configureResponsiveFullscreenUI } from "../ui/guiScaling";
import { scaledFont } from "../ui/menuFonts";
import { isAnyGamepadButtonPressed } from "../ui/ControlGlyphs";
import type { Observer } from "@babylonjs/core/Misc/observable";

export interface TitleScreenContext {
  scene: Scene;
  dispose: () => void;
}

export interface TitleScreenOptions {
  notice?: {
    message: string;
    color?: string;
  };
}

/**
 * Set up the Title Screen scene.
 * Returns a context with a dispose function for cleanup when transitioning away.
 */
export function setupTitleScreen(scene: Scene, options: TitleScreenOptions = {}): TitleScreenContext {
  if (import.meta.env.DEV) {
    (window as any).__titleNotice = options.notice?.message ?? null;
  }

  // --- Clear scene ---
  scene.clearColor = new Color4(0.02, 0.01, 0.05, 1.0);

  // --- Camera (static, looking at origin) ---
  const camera = new FreeCamera("titleCamera", new Vector3(0, 0, -20), scene);
  camera.setTarget(Vector3.Zero());

  // --- Lighting ---
  const ambientLight = new HemisphericLight("titleAmbient", new Vector3(0, 1, 0), scene);
  ambientLight.intensity = 0.3;
  ambientLight.diffuse = Color3.FromHexString(COLORS.DEEP_PURPLE);
  ambientLight.groundColor = Color3.FromHexString(COLORS.MIDNIGHT_BLUE);

  const accentLight1 = new PointLight("greenAccent", new Vector3(-5, 3, -5), scene);
  accentLight1.diffuse = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN);
  accentLight1.intensity = 0.4;

  const accentLight2 = new PointLight("copperAccent", new Vector3(5, -2, -5), scene);
  accentLight2.diffuse = Color3.FromHexString(COLORS.COPPER);
  accentLight2.intensity = 0.3;

  // --- Glow layer ---
  const glowLayer = new GlowLayer("titleGlow", scene);
  glowLayer.intensity = 0.6;

  // --- Background gears (decorative) ---
  const gears: { mesh: ReturnType<typeof MeshBuilder.CreateTorus>; speed: number }[] = [];

  const gearMaterial = new StandardMaterial("gearMat", scene);
  gearMaterial.diffuseColor = Color3.FromHexString(COLORS.BRASS);
  gearMaterial.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.15);
  gearMaterial.specularColor = Color3.FromHexString(COLORS.COPPER);

  // Large background gear
  const gear1 = MeshBuilder.CreateTorus("gear1", {
    diameter: 8,
    thickness: 0.6,
    tessellation: 24,
  }, scene);
  gear1.material = gearMaterial;
  gear1.position = new Vector3(-9, -4, 10);
  gear1.rotation.x = Math.PI * 0.15;
  gears.push({ mesh: gear1, speed: 0.15 });

  const gear2 = MeshBuilder.CreateTorus("gear2", {
    diameter: 5,
    thickness: 0.4,
    tessellation: 18,
  }, scene);
  gear2.material = gearMaterial;
  gear2.position = new Vector3(8.5, 4.2, 11);
  gear2.rotation.x = Math.PI * 0.25;
  gears.push({ mesh: gear2, speed: -0.22 });

  const gear3 = MeshBuilder.CreateTorus("gear3", {
    diameter: 12,
    thickness: 0.8,
    tessellation: 32,
  }, scene);
  gear3.material = gearMaterial;
  gear3.position = new Vector3(0, -5.5, 15);
  gear3.rotation.z = Math.PI * 0.1;
  gears.push({ mesh: gear3, speed: 0.08 });

  // --- Floating particles (bioluminescent) ---
  const particleSystem = new ParticleSystem("titleParticles", 150, scene);
  particleSystem.createPointEmitter(new Vector3(-15, -10, 0), new Vector3(15, 10, 0));
  particleSystem.minLifeTime = 3;
  particleSystem.maxLifeTime = 8;
  particleSystem.minSize = 0.03;
  particleSystem.maxSize = 0.12;
  particleSystem.emitRate = 20;
  particleSystem.gravity = new Vector3(0, 0.3, 0);
  particleSystem.color1 = Color4.FromHexString(COLORS.BIOLUMINESCENT_GREEN + "aa");
  particleSystem.color2 = Color4.FromHexString(COLORS.BIOLUMINESCENT_PURPLE + "88");
  particleSystem.colorDead = new Color4(0, 0.5, 0.3, 0);
  particleSystem.minEmitPower = 0.2;
  particleSystem.maxEmitPower = 0.8;
  particleSystem.minEmitBox = new Vector3(-12, -8, 2);
  particleSystem.maxEmitBox = new Vector3(12, 8, 6);
  particleSystem.start();

  // --- GUI overlay ---
  const gui = AdvancedDynamicTexture.CreateFullscreenUI("titleUI", true, scene);
  const disposeGuiScaling = configureResponsiveFullscreenUI(gui, scene);

  const titleFrame = new Rectangle("titleFrame");
  titleFrame.width = "900px";
  titleFrame.height = "320px";
  titleFrame.cornerRadius = 22;
  titleFrame.thickness = 2;
  titleFrame.color = "rgba(196, 155, 60, 0.9)";
  titleFrame.background = "rgba(12, 8, 24, 0.72)";
  titleFrame.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
  titleFrame.isHitTestVisible = false;
  gui.addControl(titleFrame);

  // Title container
  const titlePanel = new StackPanel("titlePanel");
  titlePanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
  titlePanel.top = "-24px";
  titlePanel.width = "820px";
  titleFrame.addControl(titlePanel);

  // Game title
  const title = new TextBlock("gameTitle");
  title.text = "The Cats of Chronos";
  title.color = COLORS.BIOLUMINESCENT_GREEN;
  title.fontSize = scaledFont(74);
  title.fontFamily = "Georgia, 'Times New Roman', serif";
  title.fontWeight = "bold";
  title.height = "100px";
  title.shadowColor = COLORS.DEEP_PURPLE;
  title.shadowOffsetX = 3;
  title.shadowOffsetY = 4;
  title.shadowBlur = 12;
  titlePanel.addControl(title);

  // Subtitle
  const subtitle = new TextBlock("subtitle");
  subtitle.text = "A co-op puzzle-platformer";
  subtitle.color = "#f0d8a2";
  subtitle.fontSize = scaledFont(30);
  subtitle.fontFamily = "Georgia, 'Times New Roman', serif";
  subtitle.fontStyle = "italic";
  subtitle.height = "58px";
  subtitle.alpha = 0.95;
  subtitle.shadowColor = "rgba(0,0,0,0.75)";
  subtitle.shadowOffsetY = 2;
  subtitle.shadowBlur = 8;
  titlePanel.addControl(subtitle);

  // Decorative divider line (via a thin rectangle)
  const divider = new Rectangle("divider");
  divider.width = "360px";
  divider.height = "3px";
  divider.thickness = 0;
  divider.background = COLORS.BRASS;
  divider.alpha = 0.75;
  // NOTE: do NOT set paddingTop/paddingBottom here. Babylon GUI paints a
  // Rectangle's `background` across its full padded bounds, so padding on
  // this 3px line rendered as a ~45px solid tan block overlapping the
  // subtitle. Spacing above/below the line is provided by the subtitle's
  // and prompt's own padding instead.
  titlePanel.addControl(divider);

  // "Press Any Key" prompt
  const prompt = new TextBlock("startPrompt");
  prompt.text = "Press Any Key or Click to Start";
  prompt.color = "#fff6df";
  prompt.fontSize = scaledFont(28);
  prompt.fontFamily = "Georgia, 'Times New Roman', serif";
  prompt.fontWeight = "bold";
  prompt.height = "62px";
  prompt.alpha = 0.92;
  // Restores the breathing room the divider used to provide via its
  // (now-removed) padding — keeps the title block visually balanced.
  prompt.paddingTop = "28px";
  prompt.shadowColor = "rgba(0,0,0,0.9)";
  prompt.shadowOffsetY = 2;
  prompt.shadowBlur = 10;
  titlePanel.addControl(prompt);

  const helperText = new TextBlock("startHelper");
  helperText.text = "Keyboard-first flow: press 1-4 on the next screen to jump into a level";
  helperText.color = "#d4c5a9";
  helperText.fontSize = scaledFont(18);
  helperText.fontFamily = "Georgia, 'Times New Roman', serif";
  helperText.height = "42px";
  helperText.textWrapping = true;
  helperText.alpha = 0.9;
  helperText.paddingTop = "8px";
  helperText.shadowColor = "rgba(0,0,0,0.8)";
  helperText.shadowOffsetY = 1;
  helperText.shadowBlur = 6;
  titlePanel.addControl(helperText);

  if (options.notice) {
    const noticeFrame = new Rectangle("titleNotice");
    noticeFrame.width = "720px";
    noticeFrame.height = "72px";
    noticeFrame.cornerRadius = 14;
    noticeFrame.thickness = 2;
    noticeFrame.color = options.notice.color ?? COLORS.BIOLUMINESCENT_GREEN;
    noticeFrame.background = "rgba(10, 8, 20, 0.82)";
    noticeFrame.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
    noticeFrame.top = "-34px";
    noticeFrame.isHitTestVisible = false;
    gui.addControl(noticeFrame);

    const noticeLabel = new TextBlock("titleNoticeLabel");
    noticeLabel.text = options.notice.message;
    noticeLabel.color = options.notice.color ?? "#f5eedb";
    noticeLabel.fontSize = 22;
    noticeLabel.fontFamily = "Georgia, 'Times New Roman', serif";
    noticeLabel.height = "56px";
    noticeLabel.textWrapping = true;
    noticeLabel.paddingLeft = "16px";
    noticeLabel.paddingRight = "16px";
    noticeLabel.shadowColor = "rgba(0,0,0,0.9)";
    noticeLabel.shadowBlur = 8;
    noticeFrame.addControl(noticeLabel);
  }

  // --- Animations ---
  let time = 0;
  let inputHandled = false;

  // Gamepad start: edge-detect button 0 (A), button 9 (Start/Menu)
  let gpStartButtonDown = false;

  const beforeRender = () => {
    time += scene.getEngine().getDeltaTime() / 1000;

    // Rotate gears
    for (const gear of gears) {
      gear.mesh.rotation.z += gear.speed * scene.getEngine().getDeltaTime() / 1000;
    }

    // Pulse the prompt text
    prompt.alpha = 0.76 + Math.sin(time * 2.5) * 0.18;

    // Slowly drift accent lights
    accentLight1.position.x = -5 + Math.sin(time * 0.3) * 2;
    accentLight2.position.y = -2 + Math.cos(time * 0.4) * 1.5;

    // Gamepad start — fire on rising edge of button 0 (A) or button 9 (Start)
    const gpPressed = isAnyGamepadButtonPressed([0, 9]);
    if (gpPressed && !gpStartButtonDown && !inputHandled) {
      inputHandled = true;
      gameEvents.emit(GameEvents.MENU_START_PRESSED);
    }
    gpStartButtonDown = gpPressed;
  };

  let beforeRenderObserver: Observer<Scene> | null = null;
  beforeRenderObserver = scene.onBeforeRenderObservable.add(beforeRender);

  // --- Input handling (any key or click transitions to level select) ---
  const handleKeyDown = (_evt: KeyboardEvent) => {
    if (inputHandled) return;
    inputHandled = true;
    gameEvents.emit(GameEvents.MENU_START_PRESSED);
  };

  const handlePointerDown = () => {
    if (inputHandled) return;
    inputHandled = true;
    gameEvents.emit(GameEvents.MENU_START_PRESSED);
  };

  window.addEventListener("keydown", handleKeyDown);
  const pointerObs = scene.onPointerObservable.add(handlePointerDown);

  // --- Dispose function ---
  const dispose = () => {
    window.removeEventListener("keydown", handleKeyDown);
    scene.onPointerObservable.remove(pointerObs);
    scene.onBeforeRenderObservable.remove(beforeRenderObserver);
    disposeGuiScaling();
    particleSystem.dispose();
    glowLayer.dispose();
    gui.dispose();
    for (const gear of gears) {
      gear.mesh.dispose();
    }
    gearMaterial.dispose();
    ambientLight.dispose();
    accentLight1.dispose();
    accentLight2.dispose();
    camera.dispose();
  };

  return { scene, dispose };
}
